import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { LocalstorageService } from './localstorage.service';
import { TierTheme, TIER_THEMES } from '../models/tier-theme.model';

const TIER_LEVEL_STORAGE_KEY = 'tierLevel';

@Injectable({
  providedIn: 'root'
})
export class TierThemeService {
  private currentThemeSubject: BehaviorSubject<TierTheme>;
  public currentTheme$: Observable<TierTheme>;

  constructor(private localStorageService: LocalstorageService) {
    // Restore last-known tier from localStorage so the header doesn't flash the
    // base (Silver) theme on reload before the tiers API returns.
    const cached = this.localStorageService.getItem(TIER_LEVEL_STORAGE_KEY);
    const cachedLevel = cached ? Number(cached) : null;
    const initialTheme =
      (cachedLevel && TIER_THEMES[cachedLevel]) || TIER_THEMES[1];

    this.currentThemeSubject = new BehaviorSubject<TierTheme>(initialTheme);
    this.currentTheme$ = this.currentThemeSubject.asObservable();
    this.applyTheme(initialTheme);
  }

  /**
   * Get the current theme
   */
  getCurrentTheme(): TierTheme {
    return this.currentThemeSubject.value;
  }

  /**
   * Get the current tier level
   */
  getCurrentTierLevel(): number {
    return this.currentThemeSubject.value.tierLevel;
  }

  /**
   * Get the current tier primary color
   */
  getCurrentColor(): string {
    return this.currentThemeSubject.value.primary;
  }

  /**
   * Get the current tier dark color
   */
  getCurrentDarkColor(): string {
    return this.currentThemeSubject.value.primaryDark;
  }

  /**
   * Reset theme to default (e.g. Tier 1). Call on logout so the next user
   * does not see the previous user's theme until their tier data loads.
   */
  resetToDefault(): void {
    const defaultTheme = TIER_THEMES[1];
    this.currentThemeSubject.next(defaultTheme);
    this.applyTheme(defaultTheme);
    this.localStorageService.removeItem(TIER_LEVEL_STORAGE_KEY);
  }

  /**
   * Set theme by tier level
   * @param tierLevel The tier level (1, 2, or 3)
   */
  setThemeByTierLevel(tierLevel: number): void {
    const theme = TIER_THEMES[tierLevel];
    if (theme) {
      this.currentThemeSubject.next(theme);
      this.applyTheme(theme);
      this.localStorageService.addItem(
        TIER_LEVEL_STORAGE_KEY,
        String(tierLevel)
      );
    } else {
      console.warn(
        `Theme for tier level ${tierLevel} not found. Using default (tier 1).`
      );
      const defaultTheme = TIER_THEMES[1];
      this.currentThemeSubject.next(defaultTheme);
      this.applyTheme(defaultTheme);
      this.localStorageService.removeItem(TIER_LEVEL_STORAGE_KEY);
    }
  }

  /**
   * Apply theme by setting CSS custom properties
   * @param theme The theme to apply
   */
  private applyTheme(theme: TierTheme): void {
    const root = document.documentElement;

    root.style.setProperty('--tier-color', theme.primary);
    root.style.setProperty('--tier-color-dark', theme.primaryDark);
    root.style.setProperty('--tier-reward-bg', theme.rewardBackground);
    root.style.setProperty(
      '--tier-earn-redeem-btn-bg',
      theme.earnRedeemButtonBg
    );
  }
}
